import React, { useEffect, useState } from 'react'
import './job-schedules-list.scss'
import classnames from 'classnames'
import clone from 'clone-deep'
import * as deepEqual from 'react-fast-compare'
import { useMountedRef } from '../../hooks/use-mounted-ref'
import { fmtDateString, fmtNumberAsDelineatedCount } from '../../helpers/formatting'
import { useAxiosCancelToken } from '../../hooks/use-axios-cancel-token'
import EntityDisplay, { EntityTitle } from '../../components/data-table-view/entity-display'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { createJobSchedule, fetchJobScheduleFilters, fetchJobSchedules, patchSchedule } from '@halo/api/jobs.api'

import {
    CheckCircleFilled,
    ClockCircleOutlined,
    CopyOutlined,
    DeleteFilled,
    EditOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    Loading3QuartersOutlined,
    PauseCircleFilled,
    PlayCircleFilled,
    ReloadOutlined,
} from '@ant-design/icons'

import { Flex, Input, Radio, Table } from 'antd'
import { Button, FilterBar } from '@pushly/aqe/lib/components'
import type { ActiveFilter, ButtonAction, FilterDefinition } from '@pushly/aqe/lib/components'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { useLocation } from 'react-router'
import queryString from 'query-string'
import { stripUndefined } from '@halo/helpers/clean-objects'
import { collateConfig, buildApiOrderConfig } from '@halo/components/data-table-view/helpers'
import { useRefState } from '@halo/hooks/use-ref-state'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import ExpandedScheduleView from '@halo/components/job-schedules-list/expanded-schedule-view'
import { useRefEffect } from '@halo/hooks/use-ref-effect'
import { DataSourceResolver } from '@halo/components/data-table-view/types'
import {
    handleScheduleAlarmSilence,
    handleScheduleAlarmUnsilence,
    handleScheduleDestroy,
    handleScheduleDisable,
    handleScheduleEnable,
} from '@halo/components/job-schedules-list/helpers'
import { DataTableContext } from '@halo/components/data-table-view/context'
import { observer } from 'mobx-react'
import { JOB_SCHEDULE_ACTION } from '@halo/components/job-schedules-list/enums'
import JobScheduleEditor from '@halo/components/job-schedules-list/job-schedule-editor'
import * as randomstring from 'randomstring'
import { SorterResult, TablePaginationConfig } from 'antd/es/table/interface'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

const JobDateTime = observer(({ date }: { date: string }) => {
    const appState = useService<AppState>(AppState)

    return !date ? (
        <>--</>
    ) : (
        <EntityDisplay
            top={{ left: <span className="job-date">{fmtDateString(date, 'll')}</span> }}
            mid={{
                left: (
                    <span className="job-time">{fmtDateString(date, 'LT z', appState.displayTimeZone ?? 'utc')}</span>
                ),
            }}
        />
    )
})

const renderBoolean = (value: boolean) =>
    value ? <CheckCircleFilled /> : <Radio style={{ pointerEvents: 'none' }} checked={false} />

const JobSchedulesList = () => {
    const appState = useService<AppState>(AppState)

    const [_mounted, runIfMounted] = useMountedRef()
    const [editorState, setEditorState] = useState<any>({ visible: false })
    const [cancelToken, resetCancelToken] = useAxiosCancelToken()
    const { search: rawQs } = useLocation()
    const qs = queryString.parse(rawQs.replace(/^\?/, ''))
    const qsJobId = qs.job_id ?? qs.jobId

    const [schedules, setSchedules] = useRefState({
        loading: false,
        data: undefined,
        pagination: {
            pageSize: 100,
            current: 1,
            total: 100,

            size: 'small',
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showQuickJumper: true,
            showTotal: (total: number) => renderTotal(total),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }
                if (type === 'page' || /^jump/.test(type)) {
                    return ''
                }

                return originalElement
            },
        } as TablePaginationConfig,
        filters: stripUndefined({
            tableSearchValue: qsJobId,
            enabled: ['true'],
        }),
        sortOrder: {
            columnKey: 'created_at',
            field: 'created_at',
            order: 'descend',
        } as SorterResult<any>,
    })

    const [paginationLinks, setPaginationLinks] = React.useState<null | IApiResponsePaginationLinks>(null)
    const [apiFilters] = useRefEffect(() => {
        return fetchJobScheduleFilters({ showLoadingScreen: false })
    }, [false])

    const [filterDefinitions, setFilterDefinitions] = useState<FilterDefinition[]>([])

    useEffect(() => {
        apiFilters.current?.then(({ ok, data }) => {
            const defs: FilterDefinition[] = []
            if (ok && Array.isArray(data.targets)) {
                defs.push({
                    key: 'target_name',
                    label: 'Target',
                    type: 'multi-select',
                    showOperatorSelect: false,
                    selectOptions: data.targets.map((tgt: any) => ({
                        value: tgt.name,
                        label: tgt.name === 'submarine-communications-cable' ? 'scc' : tgt.name,
                    })),
                })
            }
            defs.push({
                key: 'enabled',
                label: 'State',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { label: 'Enabled', value: 'true' },
                    { label: 'Disabled', value: 'false' },
                ],
            })
            setFilterDefinitions(defs)
        })
    }, [apiFilters])

    const renderTotal = (total: number): React.ReactNode => {
        const {
            pagination: { current, pageSize },
        } = clone(schedules.current)
        const start = fmtNumberAsDelineatedCount((current - 1) * pageSize + 1)
        const end = fmtNumberAsDelineatedCount(current * pageSize)
        const ttl = fmtNumberAsDelineatedCount(total)

        return total <= pageSize
            ? `Showing ${ttl} of ${ttl} Total Schedules(s)`
            : `Showing ${start}-${end} of ${ttl} Total Schedules(s)`
    }

    const fetchData: DataSourceResolver<any> = async (pagination, filters, sortOrder, extra): Promise<void> => {
        setSchedules({ ...schedules.current, loading: true })
        let data: any[] = []

        let paginationConfig = clone(collateConfig(pagination, schedules.current.pagination))
        let filtersConfig = clone(collateConfig(filters, schedules.current.filters))
        let sortConfig = clone(collateConfig(sortOrder, schedules.current.sortOrder))

        const pageSizeChanged = paginationConfig.pageSize !== schedules.current.pagination?.pageSize
        const filtersChanged = !deepEqual(filtersConfig, schedules.current.filters)
        const sortChanged = !deepEqual(sortConfig, schedules.current.sortOrder)
        const changesDetected = pageSizeChanged || filtersChanged || sortChanged

        // reset current page on filter/sort/size change
        if (changesDetected) {
            paginationConfig.current = 1
        }

        const searchVal = filtersConfig.tableSearchValue?.trim()
        const orderConfig = buildApiOrderConfig(sortConfig)

        const query = stripUndefined({
            limit: paginationConfig?.pageSize,
            page: paginationConfig?.current,
            search: !searchVal ? undefined : searchVal,
            target_name: !filtersConfig.target_name?.length ? undefined : filtersConfig.target_name.join(','),
            env: !filtersConfig.env?.length ? undefined : filtersConfig.env.join(','),
            enabled: !filtersConfig.enabled?.length ? undefined : filtersConfig.enabled.join(','),
            order: !orderConfig ? undefined : JSON.stringify(orderConfig),
        })

        resetCancelToken()
        const res = await fetchJobSchedules({
            query,
            showLoadingScreen: false,
            cancelToken: cancelToken.current,
        })

        if (res && res.ok) {
            data = res.data!
            setPaginationLinks(res.meta.links ?? paginationLinks)
        }

        runIfMounted(() => {
            setSchedules({
                loading: false,
                data,
                pagination: paginationConfig,
                filters: filtersConfig,
                sortOrder: sortConfig,
            })
        })
    }

    const handleRowAction = (action: string | number, schedule: any) => {
        if (action === JOB_SCHEDULE_ACTION.EDIT) {
            setEditorState({
                key: randomstring.generate(),
                visible: true,
                schedule,
            })
        } else if (action === JOB_SCHEDULE_ACTION.DUPLICATE) {
            let duplicateSchedule = clone(schedule)
            delete duplicateSchedule.id
            delete duplicateSchedule.name
            delete duplicateSchedule.destroyed_at
            delete duplicateSchedule.created_at

            setEditorState({
                key: randomstring.generate(),
                visible: true,
                isDuplicate: true,
                schedule: duplicateSchedule,
            })
        } else if (action === JOB_SCHEDULE_ACTION.ENABLE) {
            handleScheduleEnable(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.DISABLE) {
            handleScheduleDisable(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.DESTROY) {
            handleScheduleDestroy(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.ALARMS_SILENCE) {
            handleScheduleAlarmSilence(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_SCHEDULE_ACTION.ALARMS_UNSILENCE) {
            handleScheduleAlarmUnsilence(schedule, (ok, j) => {
                if (ok) {
                    fetchData()
                }
            })
        }
    }

    // run once on initial mount
    useEffect(() => {
        fetchData()
    }, [false])

    const getFilterBarValue = (): ActiveFilter[] => {
        const active: ActiveFilter[] = []
        const currentFilters = schedules.current.filters ?? {}
        if (currentFilters.target_name?.length > 0) {
            active.push({ key: 'target_name', operator: 'in', value: currentFilters.target_name })
        }
        if (currentFilters.enabled?.length > 0) {
            active.push({ key: 'enabled', operator: 'in', value: currentFilters.enabled })
        }
        return active
    }

    const handleFilterBarApply = (active: ActiveFilter[]) => {
        const nextTargetName = (active.find((f) => f.key === 'target_name')?.value as string[]) ?? []
        const nextEnabled = (active.find((f) => f.key === 'enabled')?.value as string[]) ?? []

        const nextFilters = stripUndefined({
            ...schedules.current.filters,
            target_name: nextTargetName.length ? nextTargetName : undefined,
            enabled: nextEnabled.length ? nextEnabled : undefined,
        })

        fetchData(schedules.current.pagination, nextFilters, schedules.current.sortOrder)
    }

    const handleSearch = (value: string) => {
        const nextFilters = stripUndefined({
            ...schedules.current.filters,
            tableSearchValue: value?.trim() || undefined,
        })
        fetchData(schedules.current.pagination, nextFilters, schedules.current.sortOrder)
    }

    const tableHeader = (
        <Flex gap={8} justify="space-between" align="center">
            <Flex flex={1} gap={8} align="center">
                <Input.Search
                    style={{ width: 300 }}
                    size="middle"
                    placeholder="Search by ID or Schedule Name"
                    enterButton={true}
                    defaultValue={schedules.current.filters?.tableSearchValue}
                    onSearch={handleSearch}
                />
                <FilterBar
                    filterDefinitions={filterDefinitions}
                    value={getFilterBarValue()}
                    onApply={handleFilterBarApply}
                    showInlineTags={false}
                    showInlineClearAll={false}
                    size="middle"
                />
                <ReloadOutlined spin={schedules.current.loading} onClick={() => fetchData()} />
            </Flex>
            {appState.currentUser?.isInternalDevUser && (
                <Flex align="center">
                    <Button
                        type="primary"
                        onClick={() => {
                            setEditorState({
                                key: randomstring.generate(),
                                visible: true,
                            })
                        }}
                    >
                        New Schedule
                    </Button>
                </Flex>
            )}
        </Flex>
    )

    return (
        <div className={classnames('jobs-manager-schedules-list', 'page')}>
            <DataTableContext.Provider
                value={{
                    loading: schedules.current.loading,
                    currentDataSource: schedules.current.data,
                    resolveDataSource: fetchData,
                }}
            >
                <div className="page-wrapper">
                    <Table
                        className="aqe-table-well"
                        title={() => tableHeader}
                        size="small"
                        bordered={false}
                        // scroll.y used to freeze header row
                        // scroll.x used to fix bug with col width and scroll.y
                        // https://github.com/ant-design/ant-design/issues/7603#issuecomment-697600114
                        scroll={{ y: 549, x: 'max-content' }}
                        rowKey="id"
                        loading={
                            !schedules.current.loading
                                ? false
                                : {
                                      indicator: <Loading3QuartersOutlined spin={true} />,
                                  }
                        }
                        dataSource={schedules.current.data}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    setSchedules({
                                        ...schedules.current,
                                        pagination: {
                                            ...schedules.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                                onNext={(_ev, _link, config) => {
                                    setSchedules({
                                        ...schedules.current,
                                        pagination: {
                                            ...schedules.current.pagination,
                                            current: config.current!,
                                        },
                                    })
                                    fetchData()
                                }}
                            />
                        )}
                        locale={{
                            emptyText: <AntdTableEmptyPlaceholder text="No Schedules" icon={<ClockCircleOutlined />} />,
                        }}
                        onChange={fetchData}
                        expandedRowRender={(schedule: any, index: number, indent: number, expanded: boolean) => (
                            <ExpandedScheduleView
                                schedule={schedule}
                                index={index}
                                indent={indent}
                                expanded={expanded}
                            />
                        )}
                    >
                        <Table.Column
                            title="Name"
                            className="name"
                            dataIndex="name"
                            render={(name, schedule: any) => {
                                return (
                                    <EntityDisplay
                                        top={{
                                            left: <EntityTitle>{name}</EntityTitle>,
                                        }}
                                        mid={{
                                            left: <span className="schedule-id">#{schedule.id}</span>,
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="target"
                            title="Target"
                            dataIndex="target"
                            render={(config) => {
                                return (
                                    <EntityDisplay
                                        top={{
                                            left: (
                                                <EntityTitle>
                                                    {config.name === 'submarine-communications-cable'
                                                        ? 'scc'
                                                        : config.name}
                                                </EntityTitle>
                                            ),
                                        }}
                                        mid={{
                                            left: <span className="target-type">{config.type}</span>,
                                        }}
                                    />
                                )
                            }}
                        />
                        <Table.Column
                            className="enabled"
                            title="Enabled"
                            dataIndex="enabled"
                            align="center"
                            render={renderBoolean}
                        />
                        <Table.Column
                            className="created_at"
                            title="Created"
                            dataIndex="created_at"
                            align="center"
                            render={(dt) => <JobDateTime date={dt} />}
                        />
                        {appState.currentUser?.isInternalDevUser && (
                            <Table.Column
                                className="actions"
                                title="Actions"
                                dataIndex="actions"
                                align="center"
                                render={(_, schedule: any) => {
                                    const { enabled, alarm_config } = schedule

                                    const actions: ButtonAction[] = [
                                        {
                                            key: JOB_SCHEDULE_ACTION.DUPLICATE,
                                            text: 'Duplicate',
                                            icon: <CopyOutlined />,
                                            onClick: () =>
                                                handleRowAction(JOB_SCHEDULE_ACTION.DUPLICATE, schedule),
                                        },
                                        {
                                            key: enabled
                                                ? JOB_SCHEDULE_ACTION.DISABLE
                                                : JOB_SCHEDULE_ACTION.ENABLE,
                                            text: enabled ? 'Disable' : 'Enable',
                                            icon: enabled ? <PauseCircleFilled /> : <PlayCircleFilled />,
                                            onClick: () =>
                                                handleRowAction(
                                                    enabled
                                                        ? JOB_SCHEDULE_ACTION.DISABLE
                                                        : JOB_SCHEDULE_ACTION.ENABLE,
                                                    schedule,
                                                ),
                                        },
                                        ...(alarm_config
                                            ? [
                                                  {
                                                      key: alarm_config.silent
                                                          ? JOB_SCHEDULE_ACTION.ALARMS_UNSILENCE
                                                          : JOB_SCHEDULE_ACTION.ALARMS_SILENCE,
                                                      text: alarm_config.silent ? 'Enable Alarm' : 'Silence Alarm',
                                                      icon: enabled ? <EyeOutlined /> : <EyeInvisibleOutlined />,
                                                      onClick: () =>
                                                          handleRowAction(
                                                              alarm_config.silent
                                                                  ? JOB_SCHEDULE_ACTION.ALARMS_UNSILENCE
                                                                  : JOB_SCHEDULE_ACTION.ALARMS_SILENCE,
                                                              schedule,
                                                          ),
                                                  } as ButtonAction,
                                              ]
                                            : []),
                                        {
                                            key: JOB_SCHEDULE_ACTION.DESTROY,
                                            text: 'Delete',
                                            icon: <DeleteFilled />,
                                            onClick: () =>
                                                handleRowAction(JOB_SCHEDULE_ACTION.DESTROY, schedule),
                                        },
                                    ]

                                    return (
                                        <Button
                                            size="small"
                                            onClick={() => handleRowAction(JOB_SCHEDULE_ACTION.EDIT, schedule)}
                                            actions={actions}
                                        >
                                            <EditOutlined />
                                            <span>Edit</span>
                                        </Button>
                                    )
                                }}
                            />
                        )}
                    </Table>
                </div>
                <JobScheduleEditor
                    key={editorState.key}
                    visible={editorState.visible}
                    mode={!editorState.schedule || editorState.isDuplicate ? 'create' : 'edit'}
                    schedule={editorState.schedule}
                    onClose={() => setEditorState({ visible: false })}
                    onSubmit={async (schedule) => {
                        let isCreateMode = !editorState.schedule
                        let res: any = {}

                        if (editorState.isDuplicate) {
                            isCreateMode = true
                        }
                        if (isCreateMode) {
                            res = await createJobSchedule(schedule, {})
                        } else {
                            res = await patchSchedule(editorState.schedule.id, schedule, {})
                        }

                        if (res.ok) {
                            simpleNotification(
                                'success',
                                isCreateMode
                                    ? 'Schedule successfully created.'
                                    : 'Schedule successfully updated.',
                                undefined,
                                undefined,
                                true,
                            )

                            setEditorState({ visible: false })
                            return fetchData()
                        } else {
                            simpleNotification(
                                'error',
                                res.error?.message ?? 'An unknown error has occurred.',
                                undefined,
                                undefined,
                                true,
                            )
                        }
                    }}
                />
            </DataTableContext.Provider>
        </div>
    )
}

export default JobSchedulesList
